import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import { UpdateSettings as BLUpdateSettings } from '../../../../utilities/BL/Admin/Overlays/Playlist';

/**
 * MODAL DE CONFIGURACIÓN OPERATIVA Y VISUAL DE ARMA LA PLAYLIST [HU-07]
 * 
 * Permite ajustar tiempos de cooldown, filtros de lenguaje inapropiado, mensajes de pausa,
 * modos de pantalla de estadio y visibilidad de votos/aficionados.
 * 
 * @component
 * @param {Object} props
 * @param {boolean} [props.isOpen=true] - Visibilidad del modal.
 * @param {Object|null} props.settings - Objeto con la configuración actual.
 * @param {Function} props.onClose - Callback al cerrar el modal.
 * @param {Function} [props.onSuccess] - Callback al guardar exitosamente la configuración.
 * @param {string|null} [props.roomId=null] - Identificador GUID de la sala activa.
 */
export const PlaylistSettingsModal = ({
    isOpen = true,
    settings = null,
    onClose = () => {},
    onSuccess = () => {},
    roomId = null
}) => {
    const { showMessage } = useFloatMessage();

    // Estado del formulario de ajustes
    const [formData, setFormData] = useState({
        cooldownSeconds: 60,
        isProfanityFilterEnabled: true,
        submissionPausedMessage: 'Las sugerencias musicales están pausadas por el momento. ¡Disfruta el evento!',
        defaultView: 1, // 1: Mostrar Todo, 2: Agrupar por Canción
        qrCount: 1, // 1: Simple (1 QR), 2: Doble (2 QRs)
        screenMode: 1, // 1: Convocatoria, 2: Reproduciendo Ahora, 3: En Cola
        showFanNameOnScreen: true,
        showVoteCountOnScreen: true
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Cargar configuración existente en el estado
    useEffect(() => {
        if (settings) {
            setFormData({
                cooldownSeconds: settings.cooldownSeconds !== undefined ? Number(settings.cooldownSeconds) : 60,
                isProfanityFilterEnabled: settings.isProfanityFilterEnabled !== undefined ? Boolean(settings.isProfanityFilterEnabled) : true,
                submissionPausedMessage: settings.submissionPausedMessage || 'Las sugerencias musicales están pausadas por el momento. ¡Disfruta el evento!',
                defaultView: settings.defaultView !== undefined ? Number(settings.defaultView) : 1,
                qrCount: settings.qrCount !== undefined ? Number(settings.qrCount) : 1,
                screenMode: settings.screenMode !== undefined ? Number(settings.screenMode) : 1,
                showFanNameOnScreen: settings.showFanNameOnScreen !== undefined ? Boolean(settings.showFanNameOnScreen) : true,
                showVoteCountOnScreen: settings.showVoteCountOnScreen !== undefined ? Boolean(settings.showVoteCountOnScreen) : true
            });
        }
    }, [settings]);

    // Manejador de teclado para cerrar con ESC
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && !isSubmitting) {
                onClose();
            }
        };

        if (isOpen) {
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, isSubmitting, onClose]);

    if (!isOpen) return null;

    /**
     * Guarda la configuración mediante la capa de negocio BL.
     */
    const handleSubmit = async (e) => {
        e.preventDefault();

        const cleanCooldown = Math.max(0, Math.min(1800, Number(formData.cooldownSeconds) || 0));
        const cleanMessage = (formData.submissionPausedMessage || '').trim();

        setIsSubmitting(true);
        try {
            const payload = {
                roomId: roomId || (settings && settings.roomId) || null,
                ...settings,
                cooldownSeconds: cleanCooldown,
                isProfanityFilterEnabled: formData.isProfanityFilterEnabled,
                submissionPausedMessage: cleanMessage,
                defaultView: Number(formData.defaultView) || 1,
                qrCount: Number(formData.qrCount) || 1,
                screenMode: Number(formData.screenMode) || 1,
                showFanNameOnScreen: formData.showFanNameOnScreen,
                showVoteCountOnScreen: formData.showVoteCountOnScreen
            };

            const response = await BLUpdateSettings(payload);

            if (response.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '⚙️ Configuración de la dinámica actualizada con éxito.'
                });
                onSuccess(response.data || payload);
                onClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'No fue posible guardar la configuración.'
                });
            }
        } catch (error) {
            console.error('[PlaylistSettingsModal] Error al actualizar configuración:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error inesperado al persistir los ajustes.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="PlaylistModal">
            {/* Backdrop */}
            <div
                className="PlaylistModal__Backdrop"
                onClick={() => !isSubmitting && onClose()}
                aria-hidden="true"
            />

            {/* Contenedor del Modal */}
            <div className="PlaylistModal__Container PlaylistModal__Container--Large" role="dialog" aria-modal="true">
                {/* Encabezado */}
                <div className="PlaylistModal__Header">
                    <div className="PlaylistModal__HeaderInfo">
                        <span className="PlaylistModal__HeaderIcon">⚙️</span>
                        <div>
                            <h2 className="PlaylistModal__Title">Configuración de Dinámica - Arma la Playlist</h2>
                            <span className="PlaylistModal__Subtitle">
                                Parámetros de participación, moderación de contenido y comportamiento visual
                            </span>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="PlaylistModal__CloseBtn"
                        onClick={onClose}
                        disabled={isSubmitting}
                        title="Cerrar modal (ESC)"
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>

                {/* Formulario */}
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                    <div className="PlaylistModal__Body">
                        {/* SECCIÓN 1: REGLAS Y TIEMPOS DE PARTICIPACIÓN */}
                        <div className="PlaylistModal__Grid">
                            {/* Cooldown */}
                            <div className="PlaylistModal__FormGroup">
                                <label className="PlaylistModal__Label">
                                    Cooldown entre Peticiones (Segundos)
                                    <span className="PlaylistModal__Hint">Min: 0s | Max: 1800s</span>
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    max="1800"
                                    step="5"
                                    className="PlaylistModal__Input"
                                    placeholder="60"
                                    value={formData.cooldownSeconds}
                                    onChange={(e) => setFormData({ ...formData, cooldownSeconds: e.target.value })}
                                    disabled={isSubmitting}
                                    required
                                />
                            </div>

                            {/* Filtro de Groserías */}
                            <div
                                className={`PlaylistModal__SwitchControl ${formData.isProfanityFilterEnabled ? 'PlaylistModal__SwitchControl--Active' : ''}`}
                                onClick={() => !isSubmitting && setFormData({ ...formData, isProfanityFilterEnabled: !formData.isProfanityFilterEnabled })}
                                style={{ height: 'fit-content', alignSelf: 'end' }}
                            >
                                <div className="PlaylistModal__SwitchLabel">
                                    <span className="PlaylistModal__SwitchTitle">
                                        🛡️ Filtro de Palabras Altisonantes
                                    </span>
                                    <span className="PlaylistModal__SwitchDesc">
                                        Bloquea o marca sugerencias con lenguaje ofensivo o prohibido.
                                    </span>
                                </div>
                                <input
                                    type="checkbox"
                                    className="form-check-input ms-3"
                                    checked={formData.isProfanityFilterEnabled}
                                    onChange={(e) => setFormData({ ...formData, isProfanityFilterEnabled: e.target.checked })}
                                    disabled={isSubmitting}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>

                        {/* Mensaje de Dinámica Pausada */}
                        <div className="PlaylistModal__FormGroup">
                            <label className="PlaylistModal__Label">
                                Mensaje de Dinámica Pausada / Cerrada
                                <span className="PlaylistModal__Hint">Visible para aficionados al bloquear peticiones</span>
                            </label>
                            <textarea
                                className="PlaylistModal__Textarea"
                                rows="2"
                                placeholder="Escribe el mensaje informativo cuando la dinámica esté en pausa..."
                                value={formData.submissionPausedMessage}
                                maxLength={500}
                                onChange={(e) => setFormData({ ...formData, submissionPausedMessage: e.target.value })}
                                disabled={isSubmitting}
                            />
                        </div>

                        {/* SECCIÓN 2: VISTAS Y MODOS DE PANTALLA */}
                        <div className="PlaylistModal__Grid">
                            {/* Vista Predeterminada en Cabina */}
                            <div className="PlaylistModal__FormGroup">
                                <label className="PlaylistModal__Label">
                                    Vista Predeterminada en Cabina de Sonido
                                </label>
                                <select
                                    className="PlaylistModal__Select"
                                    value={formData.defaultView}
                                    onChange={(e) => setFormData({ ...formData, defaultView: Number(e.target.value) })}
                                    disabled={isSubmitting}
                                >
                                    <option value={1}>📋 Mostrar Todo (Cronológico / Entrada)</option>
                                    <option value={2}>🔥 Agrupar por Canción (Más Pedidas y Votadas)</option>
                                </select>
                            </div>

                            {/* Selector de Códigos QR en Overlay */}
                            <div className="PlaylistModal__FormGroup">
                                <label className="PlaylistModal__Label">
                                    Distribución de Códigos QR en Overlay
                                </label>
                                <select
                                    className="PlaylistModal__Select"
                                    value={formData.qrCount}
                                    onChange={(e) => setFormData({ ...formData, qrCount: Number(e.target.value) })}
                                    disabled={isSubmitting}
                                >
                                    <option value={1}>📱 1 QR Simple (Lado Derecho)</option>
                                    <option value={2}>📱📱 2 QRs Dobles (Extremos Izq / Der)</option>
                                </select>
                            </div>
                        </div>

                        {/* Selector de Modo de Pantalla Gigante */}
                        <div className="PlaylistModal__FormGroup">
                            <label className="PlaylistModal__Label">
                                Modo de Visualización en Pantalla Gigante
                            </label>
                            <select
                                className="PlaylistModal__Select"
                                value={formData.screenMode}
                                onChange={(e) => setFormData({ ...formData, screenMode: Number(e.target.value) })}
                                disabled={isSubmitting}
                            >
                                <option value={1}>📢 Modo Convocatoria / Peticiones Abiertas (Call to Action)</option>
                                <option value={2}>▶️ Modo Reproduciendo Ahora (Now Playing Hero)</option>
                                <option value={3}>📜 Modo En Cola (Próximas Pistas a Sonar)</option>
                            </select>
                        </div>

                        {/* SECCIÓN 3: SWITCHES DE VISUALIZACIÓN EN PANTALLA */}
                        <div className="PlaylistModal__Grid">
                            {/* Mostrar Nombre de Aficionado */}
                            <div
                                className={`PlaylistModal__SwitchControl ${formData.showFanNameOnScreen ? 'PlaylistModal__SwitchControl--Active' : ''}`}
                                onClick={() => !isSubmitting && setFormData({ ...formData, showFanNameOnScreen: !formData.showFanNameOnScreen })}
                            >
                                <div className="PlaylistModal__SwitchLabel">
                                    <span className="PlaylistModal__SwitchTitle">
                                        👤 Nombre de Aficionado en Pantalla
                                    </span>
                                    <span className="PlaylistModal__SwitchDesc">
                                        Proyecta el crédito de quién solicitó la canción en el estadio.
                                    </span>
                                </div>
                                <input
                                    type="checkbox"
                                    className="form-check-input ms-3"
                                    checked={formData.showFanNameOnScreen}
                                    onChange={(e) => setFormData({ ...formData, showFanNameOnScreen: e.target.checked })}
                                    disabled={isSubmitting}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>

                            {/* Mostrar Conteo de Votos */}
                            <div
                                className={`PlaylistModal__SwitchControl ${formData.showVoteCountOnScreen ? 'PlaylistModal__SwitchControl--Active' : ''}`}
                                onClick={() => !isSubmitting && setFormData({ ...formData, showVoteCountOnScreen: !formData.showVoteCountOnScreen })}
                            >
                                <div className="PlaylistModal__SwitchLabel">
                                    <span className="PlaylistModal__SwitchTitle">
                                        🔥 Conteo de Votos en Pantalla
                                    </span>
                                    <span className="PlaylistModal__SwitchDesc">
                                        Muestra el total acumulado de votos populares por canción.
                                    </span>
                                </div>
                                <input
                                    type="checkbox"
                                    className="form-check-input ms-3"
                                    checked={formData.showVoteCountOnScreen}
                                    onChange={(e) => setFormData({ ...formData, showVoteCountOnScreen: e.target.checked })}
                                    disabled={isSubmitting}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Pie del Modal */}
                    <div className="PlaylistModal__Footer">
                        <button
                            type="button"
                            className="PlaylistModal__Btn PlaylistModal__Btn--Secondary"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="PlaylistModal__Btn PlaylistModal__Btn--Primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="PlaylistModal__Spinner"></span>
                                    <span>Guardando Ajustes...</span>
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check2-circle"></i>
                                    <span>Guardar Configuración</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

PlaylistSettingsModal.propTypes = {
    isOpen: PropTypes.bool,
    settings: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    roomId: PropTypes.string
};

export default PlaylistSettingsModal;
